<template>
   <!--Page alert + loader-->
   <PageStatus :alert="defaultAlert" :table-data="tableData" />


   <!--Import Data-->
   <ImportModal
      :form-import-data="formImportData"
      :alert="defaultAlert"
      @file-change="onFileImportChange"
      @import="importRecords"
      @download-template="downloadImportTemplate"
   >
      <!--Import Notes-->
      <div id="accordion">
         <div class="card">
            <div class="card-header">
               <a class="btn" data-bs-toggle="collapse" href="#collapseOne">
                  Import Process
               </a>
            </div>
            <div id="collapseOne" class="collapse show" data-bs-parent="#accordion">
               <div class="card-body">
                  <ol>
                     <li>Categories belong to the <b>company you are logged into</b>. Switch to the right company before importing.</li>
                     <li>Categories can only be imported from the <b>platform company</b>. Use this to copy your category list, for example from your local PC to the live server.</li>
                     <li>Click <b>Download Import Template</b> for the right columns, or use a file from <b>Export to Excel</b> on another server: it has the same columns.</li>
                     <li><b>Category name</b> is required and must be new. Names that already exist are skipped (deleted categories count too) and listed after the import.</li>
                     <li>Import categories <b>before</b> permissions: the permissions import finds categories by name.</li>
                  </ol>
               </div>
            </div>
         </div>
      </div>
      <!--End Import Notes-->
   </ImportModal>
   <!--Import Data-->

         <!--Filter Data Modal (shared: FilterModal.vue)-->
   <FilterModal
      :draft="filterDraft"
      v-model:search-by="tableData.searchBy"
      :view-options="viewOptions"
      :search-by-options="searchByOptions"
      :alert="defaultAlert"
      @search-by-change="changePlaceholder()"
      @apply="applyFilters"
      @clear="clearFilters"
   />
   <!--End Filter Data Modal-->

   <!-- The Modal -->
   <RecordFormModal
      :title="isEditing ? 'Update Permission Category' : 'Add Permission Category'"
      title-icon="bi bi-folder2-open"
      :is-editing="isEditing"
      :is-saving="isSaving"
      :alert="defaultAlert"
      @save="saveOrEditRecord"
      @sample="addSampleData"
      @reset="resetForm"
   >
      <!--Below id data id. User id is accessed directly in computed properties inside formData_fields function-->
      <HiddenField v-model="formInputs.id" />

      <!-- Row 1: Category name -->
      <div class="row g-3 mb-0">
         <FormField
            v-model="formInputs.category_name"
            col="col-md-12"
            label="Category Name"
            icon="fa fa-folder-open"
            placeholder="e.g. Inventory"
            tooltip="A new name, e.g. Inventory or Sales"
            required
            @input="handleInputChange"
            @change="handleInputChange"
         />
      </div>

      <!-- Row 2: Description -->
      <div class="row g-3 mb-0">
         <TextareaField
            v-model="formInputs.description"
            col="col-md-12"
            label="Description"
            icon="fa fa-align-left"
            rows="3"
            placeholder="e.g. Permissions for managing stock, products and warehouses"
            tooltip="What the permissions in this category are for"
            required
            @input="handleInputChange"
         />
      </div>
   </RecordFormModal>

   
   <h4 class="sa-core-color fw-semibold mb-2">
      <i :class="[pageIcon, 'me-2 opacity-75']"></i>{{ pageTitle }}
   </h4>
   <!--Tables-->
   <div class="card glass mt-0 bg-sa-card">
      <div class="card-body bg-sa-card-body p-0">
                  <!-- Table action row (shared: TableToolbar.vue) -->
         <TableToolbar
            can-add
            :filter-count="activeFilterCount"
            :columns="tableColumns"
            v-model:hidden-columns="hiddenColumns"
            :selected-count="tableData.selectedRecordIds.length"
            :deleted-view="tableData.recordFilter === 'deletedone'"
            :can-delete="$hasPermission('permission_categories.delete')"
            @add="clearForm()"
            @filter="openFilters"
            @clear-selection="clearSelection"
            @bulk-action="runBulkAction"
         >
            <!-- More menu -->
            <template #more>
               <li><h6 class="dropdown-header">Data</h6></li>
               <li>
                  <button type="button" class="dropdown-item" data-bs-toggle="modal" data-bs-target="#importModal">
                     <i class="fa fa-upload fa-fw me-1"></i> Import
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('xls')">
                     <i class="fa fa-file-excel-o fa-fw me-1 text-success"></i> Export to Excel
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('csv')">
                     <i class="fa fa-file-text-o fa-fw me-1 text-success"></i> Export to CSV
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('pdf')">
                     <i class="fa fa-file-pdf-o fa-fw me-1 text-danger"></i> Export to PDF
                  </button>
               </li>
            </template>
         </TableToolbar>
         <!-- Table Search Panel -->


         <!-- Table Search Panel -->
         <SearchBox
            v-model:search-query="tableData.searchQuery"
            v-model:records-per-page="tableData.recordsPerPage"
            :placeholder="tableData.searchPlaceHolderText"
            @search-input="onSearchInput"
            @search-click="onSearchButtonClick"
            @per-page-change="onRecordsPerPageChange"
         />
         <!-- What the table is currently showing -->
         <ActiveFilters
            :chips="activeFilterChips"
            @remove="removeFilterChip"
            @clear="clearAllFilterChips"
         />
         <!-- Table -->
         <div class="table-responsive">
            
            <table
    class="table table-hover align-middle mb-0"
    id="bootstrapTable"
    tabindex="0"
    @keydown.up="$moveRowSelection(-1, $event)"
    @keydown.down="$moveRowSelection(1, $event)"
    @keydown.enter="$onTableEnter($event)"
>
      <thead>
      <tr>
         <th>
            <input type="checkbox" v-model="tableData.selectAll" @change="toggleSelectAll" />
         </th>
         <th class="hide-sm-td"><span class="visually-hidden">Details</span></th>
         <th class="button hide-sm-td">No</th>
         <SortableTh v-if="isColumnVisible('category_name')" column="category_name" :table-data="tableData" @sort="orderBy">Category Name</SortableTh>
         <SortableTh v-if="isColumnVisible('description')" column="description" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Description</SortableTh>
         <SortableTh v-if="isColumnVisible('date_created')" column="date_created" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Date Created</SortableTh>
         <th class="button">Action</th>
      </tr>
   </thead>

   <tbody>
      <template v-for="(record, index) in tableData.records" :key="record.id">

         <!-- Main row -->
         <tr class="clickable-row"
             :data-record-id="record.id"
             :class="{ 'active-row main-row': tableData.selectedRecordId === record.id, 'row-just-saved': isJustSaved(record) }"
             @click="$onRowClick(record, $event)"
             @dblclick="$onRowDblClick(record, $event)"
         >
            <td>
               <input type="checkbox" v-model="tableData.selectedRecordIds" :value="record.id" />
            </td>
            <td class="expand-cell hide-sm-td"
                :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                @click="$toggleDetails(record.id)">
               <i class="fa fa-lg expand-icon ms-1"
                  :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                  :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
            </td>
            <td class="hide-sm-td">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}</td>
            <td v-if="isColumnVisible('category_name')">
               <!-- Phones: + / − and the number go in front of the category (their columns are hidden) -->
               <span
                  class="show-sm-inline expand-cell me-2"
                  :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                  @click="$toggleDetails(record.id)"
               >
                  <i class="fa fa-lg expand-icon"
                     :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                     :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
               </span>
               <span class="show-sm-inline">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}. </span>{{ record.category_name }}
            </td>
            <td v-if="isColumnVisible('description')" class="hide-sm-td">
               <span class="d-inline-block text-truncate cell-max-320" :title="record.description">
                  {{ record.description }}
               </span>
            </td>
            <td v-if="isColumnVisible('date_created')" class="hide-sm-td text-nowrap" :title="$ConvertToLocalDate(record.date_created)">{{ $friendlyDate(record.date_created) }}</td>
            <td>
               <RowActions
                  :record="record"
                  :modal-id="'deleteCrudModal' + record.id"
                  :can-soft-delete="$hasPermission('permission_categories.delete')"
                  :can-restore="$hasPermission('permission_categories.delete')"
                  :can-delete-permanently="$hasPermission('permission_categories.delete')"
                  @edit="editRecord"
                  @delete-type="setDeleteType"
               />

               <!--Delete Modal-->
               <DeleteModal
                   :modal-id="'deleteCrudModal' + record.id"
                   :title="deleteModalTitle"
                   :message="deleteModalMessage"
                   @confirm="deleteRecord(record.id)"
               />
               <!--End Delete Modal-->
            </td>
         </tr>

         <!-- Hidden expanded row -->
         <tr class="collapse-row">
            <td class="p-0"></td>
            <td class="p-0 hide-sm-td"></td>
            <td colspan="5" class="p-0">
               <div :id="'tableDetails' + record.id" class="collapse table-collapse table-details">
                  <div class="p-3 details-list">
                     <div><i class="fa fa-hashtag"></i><strong>Category Number:</strong> {{ record.permission_category_number }}</div>
                     <div><i class="fa fa-user"></i><strong>Creator Name:</strong> {{ [record.user_first_name, record.user_middle_name].filter(Boolean).join(' ') }}</div>
                     <div><i class="fa fa-building"></i><strong>Creator Company:</strong> {{ record.company_name }}</div>
                     <div><i class="fa fa-sitemap"></i><strong>Creator Branch:</strong> {{ record.branch_name }}</div>

                     <!-- Full description (the table cell is truncated) -->
                     <div><i class="fa fa-align-left"></i><strong>Description:</strong> {{ record.description }}</div>

                     <!-- Shown on phones, or always when the column is hidden (Columns button) -->
                     <div :class="{ 'show-sm-td': isColumnVisible('category_name') }"><i class="fa fa-folder-open"></i><strong>Category Name:</strong> {{ record.category_name }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('date_created') }"><i class="fa fa-calendar"></i><strong>Date Created:</strong> {{ $ConvertToLocalDate(record.date_created) }}</div>

                     <!-- Always in details -->
                     <div v-if="$hasPermission('view-location')"><i class="fa fa-location-arrow"></i><strong>Created From:</strong> {{ record.current_location }}</div>
                     <div><i class="fa fa-edit"></i><strong>Updated By:</strong> {{ record.updatedby_firstname }}</div>
                     <div><i class="fa fa-calendar"></i><strong>Date Updated:</strong> {{ this.$ConvertToLocalDate(record.date_updated) }}</div>
                     <div v-if="$hasPermission('view-ip-address')"><i class="fa fa-desktop"></i><strong>IP Address:</strong> {{ record.ip_address }}</div>
                  </div>
               </div>
            </td>
         </tr>

      </template>

      <!-- No rows: blank while loading, then the empty message in the middle -->
      <tr v-if="tableData.records.length === 0" class="table-filler">
         <td colspan="7" class="text-center align-middle" :style="{ height: fullPageHeight() }">
            <TableEmptyState
               v-if="!tableLoading"
               :has-filters="activeFilterCount > 0 || !!tableData.appliedSearchQuery"
               :can-add="true"
               :title="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'No permission categories yet'"
               :message="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'Categories group permissions, e.g. Sales or Inventory.'"
               icon="fa fa-folder-open"
               @add="clearForm()"
               @clear="clearAllFilterChips"
            />
         </td>
      </tr>
   </tbody>

   <tfoot class="table-light">
      <tr>
         <td colspan="7">
            <!-- Pagination (hidden when there are no rows; its space is kept) -->
            <div v-if="tableData.records.length === 0 && tableData.currentPage <= 1" class="pagination-placeholder"></div>
            <Pagination
               v-else
               :currentPage="tableData.currentPage"
               :totalPages="tableData.totalPages"
               :hasNextPage="tableData.hasNextPage"
               :totalRecords="tableData.totalRecords"
               :startRecord="tableData.startRecord"
               :endRecord="tableData.endRecord"
               :showFullPagination="tableData.totalRecords > 0"
               :lastKnownPage="lastKnownPage"
               @page-changed="fetchRecords"
               @loaderMessage="updateLoaderMessage"
               @last-page-request="goToLastPage"
            />
         </td>
      </tr>
   </tfoot>
</table>
                     
         </div>
         <!-- Bottom Controls: Delete & Pagination -->
      </div>
   </div>
   <!--End Tables-->
</template>
<script>
import PageStatus from "../../../components/admin/PageStatus.vue";
import TableToolbar from "../../../components/admin/TableToolbar.vue";
import FilterModal from "../../../components/admin/FilterModal.vue";
import ActiveFilters from "../../../components/admin/ActiveFilters.vue";
import Pagination from "../../../components/admin/Pagination.vue";
import TableEmptyState from "../../../components/admin/TableEmptyState.vue";
import SortableTh from "../../../components/admin/SortableTh.vue";
import DeleteModal from "../../../components/admin/DeleteModal.vue";
import RowActions from "../../../components/admin/RowActions.vue";
import SearchBox from "../../../components/admin/SearchBox.vue";
import ImportModal from "../../../components/admin/ImportModal.vue";
import RecordFormModal from "../../../components/admin/RecordFormModal.vue";
import FormField from "../../../components/admin/FormField.vue";
import SelectField from "../../../components/admin/SelectField.vue";
import HiddenField from "../../../components/admin/HiddenField.vue";
import TextareaField from "../../../components/admin/TextareaField.vue";
import permissionCategoriesMixin from "./mixins/permissionCategoriesMixin.js";
import tableMixin from "@/mixins/tableMixin.js";
import axios from 'axios';


export default {
   name: 'PermissionCategories',
   mixins: [tableMixin, permissionCategoriesMixin], // shared table methods first, then PermissionCategories' own
   components: {
      PageStatus,
      TableToolbar,
      FilterModal,
      ActiveFilters,
      Pagination,
      TableEmptyState,
      SortableTh,
      DeleteModal,
      RowActions,
      SearchBox,
      ImportModal,
      RecordFormModal,
      FormField,
      SelectField,
      HiddenField,
      TextareaField,
   },
   data() {
      return {
         // Standard data (isSaving, tableData, defaultAlert, formImportData ...) comes from tableMixin
         standardPageSetup: true,          // tableMixin sets up Enter-to-save and table shortcuts
         apiUrl: "/users/api_permission_categories.php", // used by tableMixin's fetchRecords, delete, import, export
         importTemplateFileName: "permission_categories_import_template.xlsx",
         exportFileName: "permissionCategoriesExport", // name of the exported file
         columnsKey: "permissioncategories", // saves this page's hidden columns (Columns button)
         formInputs: this.getDefaultFormInputs(),
         pageIcon: 'fa fa-folder-open', // same icon as this page in the sidebar
         // Filter window: View
         viewOptions: [
            { label: "All Records", value: "all" },
            { label: "Records By Me", value: "byme" },
            { label: "Records Deleted", value: "deletedone" },
         ],
         // Filter window: Search By
         searchByOptions: [
            { label: "Category Name", value: "category_name" },
            { label: "Description", value: "description" },
            { label: "Category Number", value: "permission_category_number" },
         ],
      };
   },
   async created() {      
      //alert(this.$store.state.isLoggedIn);
      //this.retriveUserDetails();           
      await this.fetchRecords(
                this.tableData.currentPage,
                this.tableData.currentSortColumn,
                "DESC",
                this.tableData.startDate,
                this.tableData.endDate,
                this.$loaderMessages.loaderMessageDefault,
            );
      this.$checkIfCanFetchLocation();//Only applies if location is enabled for user. If not Log user out for him to enable location 
   },
   computed: {
      // Page heading that follows the chosen filter.
      pageTitle() {
          const titles = {
              all: "Permission Categories",
              byme: "Permission Categories By Me",
              deletedone: "Permission Categories Deleted Records",
          };
          return titles[this.tableData.recordFilter] || "Permission Categories";
      },
      // Columns the user can show or hide (Columns button)
      tableColumns() {
          return [
              { key: "category_name", label: "Category Name" },
              { key: "description", label: "Description" },
              { key: "date_created", label: "Date Created" },
          ];
      },
  },
   methods: {
      getDefaultFormInputs(){
         return {
            //Form Data
            id: "",
            category_name: "",
            description: ""
         };
      },
            // Table defaults: the standard ones (tableMixin) + this page's own
      getDefaultTableData(){
         return {
            ...this.baseTableData(),
            searchBy: 'category_name',
            searchPlaceHolderText: 'Search by Category Name',
         };
      },
      // Sample button: a random category each press (names must be new, so one fixed sample would be refused the 2nd time)
      addSampleData() {
         const samples = [
            { category_name: "Inventory", description: "Permissions for managing stock, products and warehouses" },
            { category_name: "Sales", description: "Permissions for quotations, invoices and customer orders" },
            { category_name: "Purchases", description: "Permissions for suppliers, purchase orders and goods received" },
            { category_name: "Accounting", description: "Permissions for the chart of accounts, journals and reports" },
            { category_name: "Human Resources", description: "Permissions for employees, leave and payroll" },
            { category_name: "Reports", description: "Permissions for viewing and exporting reports" },
            { category_name: "Point of Sale", description: "Permissions for the till, receipts and cash drawers" },
            { category_name: "Customers", description: "Permissions for the customer list, contacts and statements" },
         ];
         const others = samples.filter((s) => s.category_name !== this.formInputs.category_name);
         const pick = others[Math.floor(Math.random() * others.length)];

         this.formInputs = {
            ...this.getDefaultFormInputs(),
            id: this.formInputs.id, // keep the record when editing
            category_name: pick.category_name,
            description: pick.description,
         };

         this.submitted = false;
         this.$clearValidationStates("#recordForm");
         this.$scrollToTopModal("recordFormModal");
      },
      saveOrEditRecord() {   
      if (this.isSaving) return;
      this.isSaving = true;
      try {      
         this.$scrollToTopModal("recordFormModal"); 
         this.submitted = true;
         const formIsValid = this.$validateForm('#recordForm');
         if (!formIsValid) { 
              this.$playErrorSound();
              this.handleAlert("starting", "Please correct the highlighted fields.", "danger", "modal");
              this.isSaving = false;
              return;
        }

         this.handleAlert("starting","Please wait submiting","info","modal");

         let formData = this.formData_fields();

         if (!this.formInputs.id) {
            this.tableData.currentPage = 1;
         }

         //return;
         const start = performance.now(); // Start time
         // Perform the POST request
         axios.post(this.apiUrl, formData, this.$handleConfig())
            .then((response) => {     
               if (response.data.status =='success') {
                  const end = performance.now(); // End time
                  const duration = end - start;
                  console.log("Time taken to receive:" + duration.toFixed(2));
                  this.handleSaveOrEditSuccess(response);
               } else {
                   this.handleError("starting", response.data.message[0], "danger", "modal");
               }
            })
            .catch((error) => {
               if (error.message === 'Network Error') {
                  this.handleAlert("ending","No internet connection. Please check your network.","danger","modal");
                } else {
                  this.handleAlert("ending","Something went wrong. Please try again later.","danger","modal");
                }
               //this.handleError("starting",error,"danger","modal");
             })
            .finally(() => {
                this.isSaving = false;
            });} catch (error) {
            this.isSaving = false;
            throw error;
        }
      },
      formData_fields(){
         let formData = new FormData();
         formData.append("id", this.formInputs.id);
         formData.append("category_name", this.formInputs.category_name);

         formData.append("description", this.formInputs.description);

         formData.append("latitude", this.$store.state.latitude);
         formData.append("longitude", this.$store.state.longitude);
         formData.append('action', 'saveOrEditRecord');
         return formData;
      },
      editRecord(records) {
         this.isEditing = true;
         this.$clearValidationStates('#recordForm');
         this.formInputs.id = records.id ?? '';
         this.formInputs.category_name = records.category_name ?? '';
         this.formInputs.description = records.description ?? '';
      },
         },
}; 
</script>